import { Alert, Anchor, Badge, Box, Button, Group, List, Paper, Skeleton, Stack, Table, Tabs, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconCar, IconCheck, IconDatabase, IconDownload, IconHammer, IconPuzzle } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useId, useState, type ReactNode } from 'react';
import type { GetVehicleDataResponse, VehicleDataBuild } from '@motionsafe/ms-console-api/admin/v1/vehicle_data_pb.js';

import { api } from '../../api/client';
import { androidAutoQuery, errorText, meQuery, useInvalidate, vehicleDataQuery } from '../../api/hooks';
import { AndroidAuto } from '../../components/android-auto';
import { ExpiryMark, expiryFlag } from '../../components/android-auto-expiry';
import { confirmFirst } from '../../components/confirm';
import { Capabilities } from '../../components/capabilities';
import { useShown } from '../../components/dialog';
import { useGridRows } from '../../components/grid';
import { locked } from '../../components/locked';
import { DrawerSection, EmptyState, Facts, PageHeader, StatStrip, StatStripSkeleton, ToolbarActions, type StatProps } from '../../components/page';
import { PaneBody, PaneDrawer, PaneHeader } from '../../components/pane';
import { useServerPages } from '../../components/pager';
import { fraction, megabytes, when } from '../../lib/format';
import { isStaff } from '../../lib/roles';
import { ICON, ICON_INLINE } from '../../theme';
import { useAccess } from '../_app';

// What the console serves tablets, for MotionSafe staff, a tab each: the VIN
// decoding database, the Android Auto certificate (components/android-auto.tsx),
// and the capabilities container (components/capabilities.tsx). The console builds the
// database on its own from NHTSA's monthly release; this page says what is
// published, what is building, what NHTSA last listed, and every build, and
// lets a MotionSafe admin build now.
// The tab in view rides in the address (`?tab=android-auto`), so a refresh or
// a shared link opens the same one. The VIN database is the default and leaves
// the address bare.
const TABS = ['vin', 'android-auto', 'capabilities'] as const;
type Tab = (typeof TABS)[number];
const isTab = (v: unknown): v is Tab => typeof v === 'string' && (TABS as readonly string[]).includes(v);

export const Route = createFileRoute('/_app/device-content')({
  validateSearch: (s: Record<string, unknown>): { tab?: Tab | undefined } => (isTab(s['tab']) && s['tab'] !== 'vin' ? { tab: s['tab'] } : {}),
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.fetchQuery(meQuery);
    if (!me || !isStaff(me)) throw redirect({ to: '/overview', search: {} });
  },
  component: DeviceContent,
});

function DeviceContent() {
  const { tab = 'vin' } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const aaFlag = expiryFlag(useQuery(androidAutoQuery()).data?.current?.presented);
  const setTab = (next: string | null) => {
    if (isTab(next)) void navigate({ search: (prev) => ({ ...prev, tab: next === 'vin' ? undefined : next }), replace: true });
  };
  return (
    <>
      <PageHeader
        title="Device content"
        description="What devices download from this console, and how it is kept current."
      />
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List aria-label="Content">
          <Tabs.Tab value="vin" leftSection={<IconDatabase {...ICON_INLINE} />}>VIN database</Tabs.Tab>
          <Tabs.Tab value="android-auto" leftSection={<IconCar {...ICON_INLINE} />} {...(aaFlag ? { rightSection: <ExpiryMark flag={aaFlag} /> } : {})}>Android Auto</Tabs.Tab>
          <Tabs.Tab value="capabilities" leftSection={<IconPuzzle {...ICON_INLINE} />}>Capabilities</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="vin" pt="xl">
          <VinDatabase />
        </Tabs.Panel>
        <Tabs.Panel value="android-auto" pt="xl">
          <AndroidAuto />
        </Tabs.Panel>
        <Tabs.Panel value="capabilities" pt="xl">
          <Capabilities />
        </Tabs.Panel>
      </Tabs>
    </>
  );
}

// The builder's steps, in order, as the page says them.
const STEPS: Record<string, string> = {
  fetch: 'Downloading the release',
  restore: 'Restoring it',
  copy: 'Copying the tables',
  finalize: 'Indexing',
  validate: 'Validating',
  golden: 'Checking golden VINs',
  compress: 'Compressing',
  // Its encrypted release (ARCHITECTURE.md §15.6), sealed by the console once the builder has stored it.
  seal: 'Encrypting',
};
const STEP_ORDER = Object.keys(STEPS);

function stepLine(step: string): string {
  const i = STEP_ORDER.indexOf(step);
  return i < 0 ? 'Starting' : `${STEPS[step]}, step ${fraction(i + 1, STEP_ORDER.length)}`;
}

/** NHTSA's `2026_09` as the month it names: "Sep 2026". */
function month(release: string): string {
  const m = /^(\d{4})_(\d{2})$/.exec(release);
  if (!m) return release;
  return new Date(Number(m[1]), Number(m[2]) - 1, 1).toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
}

function took(b: VehicleDataBuild): string | null {
  if (!b.startedAt || !b.finishedAt) return null;
  const mins = Math.round(Number(b.finishedAt.seconds - b.startedAt.seconds) / 60);
  return mins < 1 ? 'under a minute' : `${mins} min`;
}

const startedBy = (b: VehicleDataBuild) => (b.trigger === 'staff' ? b.requestedByName || 'MotionSafe staff' : 'Nightly check');

function Missing({ children }: { children: ReactNode }) {
  return <Text span inherit c="dimmed">{children}</Text>;
}

// The status word is the audit trail's (vehicle_data.build_started,
// .published, .build_failed). Color is for what needs attention: a build
// running, a build that failed. The published build devices are offered
// carries a quiet check; an earlier one that was superseded recedes.
function Status({ build, inUse }: { build: VehicleDataBuild; inUse: boolean }) {
  if (build.status === 'building') return <Badge color="warning">building</Badge>;
  if (build.status === 'failed') return <Badge color="danger">failed</Badge>;
  if (inUse) {
    return (
      <Group gap={6} wrap="nowrap">
        <IconCheck {...ICON_INLINE} color="var(--mantine-color-success-text)" aria-label="Offered to devices" />
        <Text span size="sm">published</Text>
      </Group>
    );
  }
  return <Text span size="sm" c="dimmed">published</Text>;
}

function stats(d: GetVehicleDataResponse): StatProps[] {
  const published = d.published;
  const latestBuilt = !!published && published.release === d.latestRelease;
  return [
    {
      label: 'Published',
      value: published ? month(published.release) : 'none',
      hint: published ? `Built ${when(published.finishedAt)}` : 'Devices get no database until a build is published',
      tone: published ? 'neutral' : 'bad',
    },
    {
      label: 'Devices with it',
      value: fraction(d.devicesCurrent, d.devicesTotal),
      hint: 'Active, assigned devices that have said so',
      tone: !published ? 'neutral' : d.devicesCurrent >= d.devicesTotal ? 'ok' : 'warn',
    },
    {
      label: "NHTSA's latest",
      value: d.latestRelease ? month(d.latestRelease) : 'unknown',
      hint: d.checkError
        ? `The last check could not read NHTSA: ${d.checkError}`
        : d.checkedAt
          ? `Checked ${when(d.checkedAt, { relative: true })}${d.latestRelease && !latestBuilt && !d.building ? ', not built yet' : ''}`
          : 'Not checked yet; the check runs nightly at 03:15 UTC',
      tone: d.checkError ? 'warn' : 'neutral',
    },
    {
      label: 'Building',
      value: d.building ? month(d.building.release) : 'nothing',
      hint: d.building ? stepLine(d.building.step) : 'The next build starts when NHTSA lists a new release',
      tone: d.building ? 'warn' : 'neutral',
    },
  ];
}

const SKELETON_WIDTHS = [150, 96, 96, 80, 140];

function VinDatabase() {
  const access = useAccess();
  const write = access.may('RequestBuild');
  const info = useQuery(vehicleDataQuery());
  const d = info.data;
  // Re-read every thirty seconds while a build runs, so its step moves on the page.
  const list = useServerPages({ key: ['vehicleData', 'builds'], narrowed: '', read: (page) => api.vehicleData.listBuilds(page), noun: 'builds', refetchInterval: d?.building ? 30_000 : false });
  const builds = list.query;
  const inv = useInvalidate();
  // The build whose details are open, as it was clicked; refreshed from the page while it is on it.
  const [opened, setOpened] = useState<VehicleDataBuild | null>(null);
  const noteId = useId();

  const all = builds.data?.builds ?? [];
  const gridRow = useGridRows(all.map((b) => b.id));
  const shown = opened ? all.find((b) => b.id === opened.id) ?? opened : null;
  const pane = useShown(shown);
  const publishedId = d?.published?.id;

  const build = useMutation({
    mutationFn: () => api.vehicleData.requestBuild({}),
    onSuccess: () => {
      void inv('vehicleData');
      notifications.show({ message: 'Build started. It takes a few minutes; its progress shows here.' });
    },
    onError: (e) => notifications.show({ message: errorText(e), color: 'danger' }),
  });

  // Why Build now cannot be used, when it cannot, and who can change that.
  const note = !write
    ? access.caption('RequestBuild')
    : d && !d.builderConnected
      ? 'No builder is connected.'
      : d?.building
        ? 'A build is running.'
        : undefined;

  const confirmBuild = () => {
    if (!d) return;
    // NHTSA is asked again when the build starts; this is what it last listed.
    const again = !!d.latestRelease && d.published?.release === d.latestRelease;
    confirmFirst({
      title: 'Build the VIN database now?',
      body: again
        ? `This builds NHTSA's newest release, ${month(d.latestRelease)}, which is the one published. The new build is a new file, so every device downloads it once it passes its checks.`
        : `This builds NHTSA's newest release${d.latestRelease ? `, ${month(d.latestRelease)}` : ''}. It takes a few minutes, and devices are offered it once it passes its checks.`,
      label: 'Build now',
      onConfirm: () => build.mutateAsync(),
    });
  };

  const action = note
    ? <Button size="sm" variant="default" leftSection={<IconHammer {...ICON_INLINE} />} {...locked(noteId)}>Build now</Button>
    : <Button size="sm" leftSection={<IconHammer {...ICON_INLINE} />} onClick={confirmBuild} disabled={build.isPending}>Build now</Button>;

  return (
    <>
      {/* The same band as every other page's counts (PageHeader), here under the tab it belongs to. */}
      <Box mb="xl" className="stats-band">
        {d ? <StatStrip stats={stats(d)} bare /> : <StatStripSkeleton count={4} bare />}
      </Box>

      {d && !d.builderConnected && (
        <Alert color="warning" mb="lg" role="status" icon={<IconAlertTriangle {...ICON} />} title="Builds cannot run">
          <Text size="sm">This console cannot reach its builder, so nothing new is built and devices keep the database that is published. A MotionSafe engineer deploys the builder (README, The VIN decoding database).</Text>
        </Alert>
      )}

      <Group align="center" gap="sm" mb="md" wrap="wrap">
        <Text size="sm" c="dimmed" flex={1} miw={0}>Every build, newest first. The nightly check builds each monthly NHTSA release.</Text>
        <ToolbarActions note={note} noteId={noteId}>{action}</ToolbarActions>
      </Group>

      <Paper p={0} style={{ overflow: 'hidden' }}>
        {builds.data && all.length === 0 ? (
          <EmptyState
            icon={IconDatabase}
            title="No builds yet"
            body="The nightly check builds NHTSA's newest release at 03:15 UTC, or a MotionSafe admin can build it now."
          />
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table role="grid" layout="fixed" aria-label="Builds of the VIN database" aria-busy={builds.isPending} highlightOnHover={all.length > 0}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={190}>Started</Table.Th><Table.Th w={120}>Release</Table.Th><Table.Th w={130}>Status</Table.Th><Table.Th w={110}>Size</Table.Th><Table.Th>Started by</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {builds.isPending && Array.from({ length: 3 }, (_, i) => (
                  <Table.Tr key={i} aria-hidden="true">
                    {SKELETON_WIDTHS.map((w, j) => <Table.Td key={j}><Skeleton height={14} width={w} /></Table.Td>)}
                  </Table.Tr>
                ))}
                {all.map((b) => (
                  <Table.Tr
                    key={b.id}
                    {...gridRow(b.id, () => setOpened(b))}
                    onClick={() => setOpened(b)}
                    style={{ cursor: 'pointer' }}
                    aria-label={`${month(b.release)} build, ${b.status}`}
                  >
                    <Table.Td className="num" style={{ whiteSpace: 'nowrap' }}>{when(b.startedAt)}</Table.Td>
                    <Table.Td className="num">{month(b.release)}</Table.Td>
                    <Table.Td><Status build={b} inUse={b.id === publishedId} /></Table.Td>
                    <Table.Td className="num">{b.gzSizeBytes > 0n ? megabytes(b.gzSizeBytes) : <Missing>{b.status === 'building' ? 'building' : 'not built'}</Missing>}</Table.Td>
                    <Table.Td>{startedBy(b)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
        {list.footer}
      </Paper>

      <PaneDrawer opened={pane.opened} onClose={() => setOpened(null)}>
        {pane.shown && <BuildPane key={pane.shown.id} build={pane.shown} inUse={pane.shown.id === publishedId} />}
      </PaneDrawer>
    </>
  );
}

// The file itself, for staff to use in local development: the same gzip
// tablets download. Only the newest three published builds are stored.
function DownloadFile({ build: b }: { build: VehicleDataBuild }) {
  if (!b.downloadable) return <>{megabytes(b.gzSizeBytes)}, <Missing>no longer stored</Missing></>;
  return (
    <Anchor href={`/staff/vehicle-data/${b.publishedId}/vpic.lite.db.gz`} download size="sm">
      <Group component="span" gap={6} wrap="nowrap">
        <IconDownload {...ICON_INLINE} aria-hidden="true" />
        <span className="num">{megabytes(b.gzSizeBytes)} gzip</span>
      </Group>
    </Anchor>
  );
}

// One build, as a page of the case file: what it was, the file it made, and
// for one that failed, what went wrong.
function BuildPane({ build: b, inUse }: { build: VehicleDataBuild; inUse: boolean }) {
  return (
    <>
      <PaneHeader
        title={`${month(b.release)} build`}
        subtitle={<Text span className="id" size="sm">{b.publishedId || b.id}</Text>}
        state={<><Status build={b} inUse={inUse} />{inUse && <Text span size="sm" c="dimmed">Offered to devices</Text>}</>}
      />
      <PaneBody>
        <DrawerSection title="Build">
          <Facts rows={[
            { label: 'NHTSA release', value: `vPICList_lite_${b.release}`, mono: true },
            { label: 'Started', value: when(b.startedAt) },
            { label: 'Started by', value: startedBy(b) },
            ...(b.status === 'building' ? [{ label: 'Now', value: stepLine(b.step) }] : []),
            ...(b.finishedAt ? [{ label: b.status === 'failed' ? 'Failed' : 'Finished', value: when(b.finishedAt) }] : []),
            ...(took(b) ? [{ label: 'Took', value: took(b)! }] : []),
          ]} />
        </DrawerSection>
        {b.status === 'published' && (
          <DrawerSection title="File">
            <Facts rows={[
              { label: 'Published as', value: b.publishedId, mono: true },
              { label: 'Encrypted release', value: b.releaseId || <Missing>none</Missing>, mono: !!b.releaseId },
              { label: 'Download', value: <DownloadFile build={b} /> },
              { label: 'Database', value: megabytes(b.dbSizeBytes) },
              { label: 'Database sha256', value: b.dbSha256, mono: true },
              { label: 'Golden VINs', value: `${b.goldenVins} VINs, ${b.goldenRows} rows as NHTSA decodes them` },
            ]} />
          </DrawerSection>
        )}
        {b.status === 'failed' && (
          <DrawerSection title="What went wrong">
            <Stack gap="sm">
              <Text size="sm">{b.error}</Text>
              {b.problems.length > 0 && (
                <List size="sm" spacing={4}>
                  {b.problems.map((p) => <List.Item key={p}>{p}</List.Item>)}
                </List>
              )}
              <Text size="sm" c="dimmed">Devices keep the database that is published.</Text>
            </Stack>
          </DrawerSection>
        )}
      </PaneBody>
    </>
  );
}
